/**
 * GovernancePage — CI/CD governance status dashboard.
 *
 * Shows governance setup status, drift detection, and links to the unified
 * Findings page for detailed violation browsing.
 */
import * as React from 'react';
import {
  ArrowSquareOut,
  ChartLineUp,
  CheckCircle,
  CircleNotch,
  GitPullRequest,
  ShieldCheck,
  ShieldWarning,
  Warning,
} from '@phosphor-icons/react';
import { Button, Card, Stack, Tabs, Text } from '@fragments-sdk/ui';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from 'convex/react';
import { api } from '@/convex';
import { failOpenSummary } from '@/lib/govern/contract-verdict';
import { DashboardLayout, StatusDashboardChrome } from '@/components/layout';
import { buildFindingsPath, buildProjectSearch } from '@/lib/routes';
import { postJson, testClerkUserHeaders } from '@/lib/client-http';
import { useProjectContext } from '@/hooks/useProjectContext';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useClerkUser } from '@/hooks/useClerkUser';
import { GovernanceSetupGuide } from './governance-setup';
import { GovernanceRules } from './governance-rules';
import { GovernanceWaivers } from './governance-waivers';
import { RuleCatalogPanel } from './rule-catalog-panel';
import { ContractGateControl } from './contract-gate-control';
import {
  hasGovernanceAutomation,
  type PlanId,
} from '../../../../convex/billing/plans';
import s from './governance.module.scss';

export function GovernancePage() {
  return (
    <DashboardLayout activeNav="Governance" flush>
      <GovernanceInner />
    </DashboardLayout>
  );
}

function GovernanceInner() {
  const navigate = useNavigate();
  const { user } = useClerkUser();
  const {
    selectedProjectId,
    selectedProject,
    resolvedSlug,
    isLoading: projectLoading,
  } = useProjectContext();
  const { orgId, org } = useOrgContext();
  const orgSlug = org?.slug;
  const plan = (org?.plan ?? 'free') as PlanId;
  const canDetectGovernance = hasGovernanceAutomation(plan);

  const binding = useQuery(
    api.projects.getPrimaryBinding,
    selectedProjectId ? { projectId: selectedProjectId } : 'skip',
  );

  const counts = useQuery(
    api.findings.getCountsSummary,
    selectedProjectId
      ? { projectId: selectedProjectId, clerkUserId: user?.id }
      : 'skip',
  );
  const trend = useQuery(
    api.findingsHistory.getGovernanceTrend,
    selectedProjectId
      ? {
          projectId: selectedProjectId,
          clerkUserId: user?.id,
          source: 'ci',
          limit: 8,
        }
      : 'skip',
  );
  const ciCounts = counts?.ci;
  const totalCounts = counts?.all;

  // Fail-open observability (brief 03): surface the latest CI run's downgrades
  // — occurrences the gate let through unverified, or an unpinnable contract.
  const latestCiCommit = trend?.items?.[0]?.commitSha;
  const latestVerdict = useQuery(
    api.contractVerdict.forCommit,
    selectedProjectId && latestCiCommit
      ? { projectId: selectedProjectId, commitSha: latestCiCommit }
      : 'skip',
  );
  const failOpen = latestVerdict ? failOpenSummary(latestVerdict) : null;
  const failOpenNotice =
    failOpen && (failOpen.unverified > 0 || failOpen.contractUnpinned)
      ? failOpen.contractUnpinned
        ? {
            title: 'Latest scan is not pinned to a contract',
            desc: 'The gate cannot enforce until a contract version is published — findings stay advisory.',
          }
        : {
            title: `${failOpen.unverified} occurrence${
              failOpen.unverified === 1 ? '' : 's'
            } failed open in the latest scan`,
            desc: 'These could not be verified against the PR head, so the gate let them through. A genuine bypass may be among them.',
          }
      : null;

  // Auto-detect governance status when the page loads if ciStatus
  // is unknown and a repo is connected. The detect endpoint updates
  // governanceCiStatus in Convex, which reactively re-renders this page.
  const autoDetected = React.useRef(false);
  const [detecting, setDetecting] = React.useState(false);
  const ciStatus = selectedProject?.governanceCiStatus;
  const repoFullName = binding?.repoFullName;

  React.useEffect(() => {
    if (autoDetected.current) return;
    if (!canDetectGovernance) return;
    if (!orgId || !selectedProjectId || !repoFullName) return;
    if (ciStatus && ciStatus !== 'not_configured') return;

    autoDetected.current = true;
    setDetecting(true);
    postJson(
      '/api/govern/detect',
      {
        orgId,
        projectId: selectedProjectId,
        repoFullName,
      },
      'Detection failed',
      { headers: testClerkUserHeaders(user?.id) },
    )
      .catch(() => {})
      .finally(() => setDetecting(false));
  }, [
    canDetectGovernance,
    orgId,
    selectedProjectId,
    repoFullName,
    ciStatus,
    user?.id,
  ]);

  if (projectLoading || detecting) {
    return (
      <Text as="div" size="sm" color="tertiary" className={s.loadingState}>
        <CircleNotch size={18} className={s.spinIcon} />
        {detecting
          ? 'Checking governance status\u2026'
          : 'Loading governance data\u2026'}
      </Text>
    );
  }

  const isSetupNeeded =
    !ciStatus ||
    ciStatus === 'not_configured' ||
    ciStatus === 'pending_pr' ||
    ciStatus === 'broken';

  if (isSetupNeeded && (ciCounts?.open ?? 0) === 0) {
    return <GovernanceSetupGuide />;
  }

  const lastSeen = selectedProject?.governanceCiLastSeenAt;
  const lastSeenLabel = lastSeen
    ? new Date(lastSeen).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;
  const statusLabel =
    ciStatus === 'active'
      ? 'Active'
      : ciStatus === 'degraded'
        ? 'Degraded'
        : ciStatus === 'broken'
          ? 'Broken'
          : ciStatus === 'pending_pr'
            ? 'Pending PR'
            : ciStatus === 'disabled'
              ? 'Disabled'
              : 'Not Configured';

  function goToFindings(view?: string) {
    if (!orgSlug) return;
    navigate({
      to: buildFindingsPath(orgSlug),
      search: buildProjectSearch(resolvedSlug, view ? { view } : undefined),
    } as unknown as Parameters<typeof navigate>[0]);
  }

  return (
    <StatusDashboardChrome
      className={s.statusDashboard}
      bodyClassName={s.statusBody}
      header={{
        title: 'Governance',
        description: 'CI/CD pipeline health and finding overview',
      }}
    >
      <Tabs defaultValue="overview" className={s.governanceTabs}>
        <Tabs.List className={s.governanceTabList}>
          <Tabs.Tab value="overview">Overview</Tabs.Tab>
          <Tabs.Tab value="ci-runs">CI runs</Tabs.Tab>
          <Tabs.Tab value="rules">Rules</Tabs.Tab>
          <Tabs.Tab value="waivers">Waivers</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" flush className={s.governanceTabPanel}>
          <Stack gap="lg">
            <Stack direction="row" wrap gap="md" className={s.statusCards}>
              <Card
                as="section"
                variant="outlined"
                padding="none"
                className={s.statusCard}
                data-status={ciStatus ?? 'active'}
              >
                <div className={s.statusCardIcon}>
                  {ciStatus === 'active' ? (
                    <CheckCircle size={22} weight="fill" />
                  ) : ciStatus === 'broken' || ciStatus === 'degraded' ? (
                    <Warning size={22} weight="fill" />
                  ) : (
                    <ShieldCheck size={22} weight="duotone" />
                  )}
                </div>
                <Stack className={s.statusCardContent} gap="xs">
                  <Text
                    as="div"
                    size="2xs"
                    weight="semibold"
                    color="tertiary"
                    className={s.statusCardLabel}
                  >
                    Pipeline Status
                  </Text>
                  <Text
                    as="div"
                    size="sm"
                    weight="semibold"
                    className={s.statusCardValue}
                  >
                    {statusLabel}
                  </Text>
                  {lastSeenLabel && (
                    <Text
                      as="div"
                      size="xs"
                      color="tertiary"
                      className={s.statusCardMeta}
                    >
                      Last run: {lastSeenLabel}
                    </Text>
                  )}
                </Stack>
              </Card>

              <Button
                variant="outlined"
                className={s.statusCard}
                data-clickable
                onClick={() => goToFindings('ci')}
              >
                <div className={s.statusCardIcon}>
                  <GitPullRequest size={22} />
                </div>
                <Stack className={s.statusCardContent} gap="xs">
                  <Text
                    as="div"
                    size="2xs"
                    weight="semibold"
                    color="tertiary"
                    className={s.statusCardLabel}
                  >
                    CI Findings
                  </Text>
                  <Text
                    as="div"
                    size="sm"
                    weight="semibold"
                    className={s.statusCardValue}
                  >
                    {ciCounts?.open ?? 0} open
                  </Text>
                  <Text
                    as="div"
                    size="xs"
                    color="tertiary"
                    className={s.statusCardMeta}
                  >
                    {ciCounts?.resolved ?? 0} resolved
                  </Text>
                </Stack>
                <ArrowSquareOut size={14} className={s.statusCardArrow} />
              </Button>

              <Button
                variant="outlined"
                className={s.statusCard}
                data-clickable
                onClick={() => goToFindings()}
              >
                <div className={s.statusCardIcon}>
                  <ShieldWarning size={22} />
                </div>
                <Stack className={s.statusCardContent} gap="xs">
                  <Text
                    as="div"
                    size="2xs"
                    weight="semibold"
                    color="tertiary"
                    className={s.statusCardLabel}
                  >
                    All Findings
                  </Text>
                  <Text
                    as="div"
                    size="sm"
                    weight="semibold"
                    className={s.statusCardValue}
                  >
                    {totalCounts?.open ?? 0} open
                  </Text>
                  <Text
                    as="div"
                    size="xs"
                    color="tertiary"
                    className={s.statusCardMeta}
                  >
                    {totalCounts?.resolved ?? 0} resolved,{' '}
                    {totalCounts?.ignored ?? 0} ignored
                  </Text>
                </Stack>
                <ArrowSquareOut size={14} className={s.statusCardArrow} />
              </Button>
            </Stack>

            {selectedProjectId && (
              <ContractGateControl projectId={selectedProjectId} />
            )}

            {failOpenNotice && (
              <div className={s.brokenBanner}>
                <ShieldWarning
                  size={18}
                  weight="fill"
                  className={s.brokenBannerIcon}
                />
                <Stack gap="xs">
                  <Text as="div" size="sm" weight="semibold">
                    {failOpenNotice.title}
                  </Text>
                  <Text as="p" size="xs" color="secondary">
                    {failOpenNotice.desc}
                  </Text>
                </Stack>
              </div>
            )}

            {ciStatus === 'pending_pr' &&
              selectedProject?.governanceCiPrUrl && (
                <Text as="div" size="sm" className={s.pendingPrBanner}>
                  <GitPullRequest size={16} />
                  <Text as="span" size="sm">
                    A pull request has been created to enable governance.
                  </Text>
                  <a
                    href={selectedProject.governanceCiPrUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View PR <ArrowSquareOut size={12} />
                  </a>
                </Text>
              )}

            <Card
              as="section"
              variant="outlined"
              padding="none"
              className={s.overviewPanel}
            >
              <Stack
                direction={{ base: 'column', md: 'row' }}
                justify="between"
                gap="md"
                className={s.overviewPanelInner}
              >
                <Stack gap="xs">
                  <Text as="h2" size="md" weight="semibold">
                    {getOverviewTitle(
                      ciCounts?.open ?? 0,
                      totalCounts?.open ?? 0,
                    )}
                  </Text>
                  <Text
                    as="p"
                    size="sm"
                    color="secondary"
                    className={s.overviewPanelDesc}
                  >
                    {getOverviewDescription(
                      ciCounts?.open ?? 0,
                      totalCounts?.open ?? 0,
                    )}
                  </Text>
                </Stack>
                <Stack direction="row" wrap gap="sm">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => goToFindings('ci')}
                  >
                    View CI/CD findings
                    <ArrowSquareOut size={13} />
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    size="sm"
                    onClick={() => goToFindings()}
                  >
                    View all findings
                    <ArrowSquareOut size={13} />
                  </Button>
                </Stack>
              </Stack>
            </Card>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="ci-runs" flush className={s.governanceTabPanel}>
          {trend && trend.items.length > 0 ? (
            <GovernanceTrendPanel
              items={trend.items}
              onOpenCi={() => goToFindings('ci')}
            />
          ) : (
            <EmptyGovernancePanel
              title="No CI runs yet"
              description="Once the governance workflow reports back, recent CI run history appears here."
            />
          )}
        </Tabs.Panel>

        <Tabs.Panel value="rules" flush className={s.governanceTabPanel}>
          <Stack gap="lg">
            {selectedProjectId && (
              <GovernanceRules projectId={selectedProjectId} />
            )}
            <RuleCatalogPanel />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="waivers" flush className={s.governanceTabPanel}>
          {selectedProjectId && (
            <GovernanceWaivers projectId={selectedProjectId} showEmptyState />
          )}
        </Tabs.Panel>
      </Tabs>
    </StatusDashboardChrome>
  );
}

function EmptyGovernancePanel({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <Card
      as="section"
      variant="outlined"
      padding="none"
      className={s.emptyPanel}
    >
      <Stack gap="xs">
        <Text as="h2" size="sm" weight="semibold">
          {title}
        </Text>
        <Text as="p" size="sm" color="secondary" className={s.emptyPanelDesc}>
          {description}
        </Text>
      </Stack>
    </Card>
  );
}

function getOverviewTitle(ciOpen: number, totalOpen: number) {
  if (ciOpen > 0) {
    return `${ciOpen} CI finding${ciOpen === 1 ? '' : 's'} need triage`;
  }
  if (totalOpen > 0) {
    return 'CI is clean, but repository findings remain';
  }
  return 'Governance is quiet';
}

function getOverviewDescription(ciOpen: number, totalOpen: number) {
  if (ciOpen > 0) {
    return 'Start with CI-sourced findings because they represent policy drift currently blocking or warning in pull requests.';
  }
  if (totalOpen > 0) {
    return 'Use the broader findings queue to decide whether repo-scan issues should become CI policy or stay advisory.';
  }
  return 'No open findings are currently reporting. Review policy readiness or check the rule catalog before tightening enforcement.';
}

type TrendRun = {
  _id: string;
  capturedAt: number;
  branch?: string;
  commitSha?: string;
  prNumber?: number;
  groupCount: number;
  occurrenceCount: number;
  classnamesScanned?: number;
  classnamesDynamic?: number;
  classnamesResolved?: number;
  severityCounts: {
    critical: number;
    serious: number;
    moderate: number;
    minor: number;
  };
  statusCounts: {
    introduced: number;
    persistent: number;
    resolved: number;
    suppressed: number;
  };
};

function GovernanceTrendPanel({
  items,
  onOpenCi,
}: {
  items: TrendRun[];
  onOpenCi: () => void;
}) {
  const latest = items[0];
  const prior = items[1];
  const delta = prior ? latest.occurrenceCount - prior.occurrenceCount : null;

  return (
    <Card
      as="section"
      variant="outlined"
      padding="none"
      className={s.trendSection}
    >
      <Stack gap="md">
        <Stack
          direction="row"
          justify="between"
          align="start"
          gap="md"
          className={s.trendHeader}
        >
          <Stack gap="xs">
            <Text as="h2" size="sm" weight="semibold" className={s.trendTitle}>
              <ChartLineUp size={16} />
              Recent CI runs
            </Text>
            <Text as="p" size="xs" color="secondary" className={s.trendDesc}>
              {delta === null
                ? `${latest.occurrenceCount} occurrences in the latest run`
                : formatDelta(delta)}
              {latest.classnamesScanned != null && latest.classnamesScanned > 0
                ? ` · Classes ${latest.classnamesScanned} scanned, ${latest.classnamesDynamic ?? 0} dynamic, ${latest.classnamesResolved ?? 0} resolved`
                : ''}
            </Text>
          </Stack>
          <Button type="button" variant="outlined" size="xs" onClick={onOpenCi}>
            Open findings
            <ArrowSquareOut size={12} />
          </Button>
        </Stack>

        <div className={s.trendRows}>
          {items.map((item) => (
            <TrendRunRow key={item._id} item={item} />
          ))}
        </div>
      </Stack>
    </Card>
  );
}

function TrendRunRow({ item }: { item: TrendRun }) {
  const totalStatus =
    item.statusCounts.introduced +
    item.statusCounts.persistent +
    item.statusCounts.resolved +
    item.statusCounts.suppressed;
  const totalSeverity =
    item.severityCounts.critical +
    item.severityCounts.serious +
    item.severityCounts.moderate +
    item.severityCounts.minor;

  return (
    <div className={s.trendRow}>
      <div className={s.trendRunMeta}>
        <Text as="span" size="xs" weight="medium" className={s.trendRunDate}>
          {formatRunDate(item.capturedAt)}
        </Text>
        <Text as="span" size="2xs" color="tertiary" className={s.trendRunRef}>
          {formatRunRef(item)}
        </Text>
      </div>
      <div className={s.trendBars} aria-label="Finding status mix">
        <TrendSegment
          value={item.statusCounts.introduced}
          total={totalStatus}
          tone="introduced"
        />
        <TrendSegment
          value={item.statusCounts.persistent}
          total={totalStatus}
          tone="persistent"
        />
        <TrendSegment
          value={item.statusCounts.resolved}
          total={totalStatus}
          tone="resolved"
        />
        <TrendSegment
          value={item.statusCounts.suppressed}
          total={totalStatus}
          tone="suppressed"
        />
      </div>
      <div className={s.trendCounts}>
        <Text as="strong" size="sm" weight="semibold">
          {item.occurrenceCount}
        </Text>
        <Text as="span" size="xs" color="tertiary">
          {item.groupCount} groups, {totalSeverity} active
        </Text>
      </div>
    </div>
  );
}

function TrendSegment({
  value,
  total,
  tone,
}: {
  value: number;
  total: number;
  tone: 'introduced' | 'persistent' | 'resolved' | 'suppressed';
}) {
  const width =
    total > 0 ? Math.max((value / total) * 100, value > 0 ? 6 : 0) : 0;
  return (
    <span
      className={s.trendSegment}
      data-tone={tone}
      style={{ width: `${width}%` }}
      title={`${value} ${tone}`}
    />
  );
}

function formatRunDate(value: number) {
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRunRef(item: TrendRun) {
  if (item.prNumber !== undefined) return `PR #${item.prNumber}`;
  if (item.branch) return item.branch;
  if (item.commitSha) return item.commitSha.slice(0, 7);
  return 'CI run';
}

function formatDelta(delta: number) {
  if (delta === 0) return 'No occurrence change from the previous run';
  const direction = delta > 0 ? 'more' : 'fewer';
  return `${Math.abs(delta)} ${direction} occurrences than the previous run`;
}
